import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import {
  Mail, Send, Users, Clock, History, Repeat, Shuffle, Plus, Trash2,
  Play, Pause, RefreshCw, Eye, Loader2, Upload, FileText,
} from 'lucide-react';

const TABS = [
  { id: 'compose',   label: 'Compose',   icon: Send },
  { id: 'recurring', label: 'Recurring', icon: Repeat },
  { id: 'contacts',  label: 'Contacts',  icon: Users },
  { id: 'history',   label: 'History',   icon: History },
];

const EmailBlasts = () => {
  const [tab, setTab] = useState('compose');
  return (
    <div className="space-y-6" data-testid="email-blasts">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Mail className="h-6 w-6 text-electric" /> Email Blasts
          </h2>
          <p className="text-sm text-muted-foreground">
            Compose branded email announcements &amp; manage your master contact list.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/10">
          {TABS.map(({ id, label, icon: I }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                tab === id ? 'bg-electric text-background' : 'text-foreground/70 hover:text-foreground hover:bg-white/5'
              }`}
              data-testid={`email-tab-${id}`}
            >
              <I className="h-4 w-4" /> {label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {tab === 'compose'   && <ComposeTab />}
      {tab === 'recurring' && <RecurringTab />}
      {tab === 'contacts'  && <ContactsTab />}
      {tab === 'history'   && <HistoryTab />}
    </div>
  );
};

// ─── Compose Tab ──────────────────────────────────────────────────────────

const emptyBlast = () => ({
  kind: 'custom',
  subject: '',
  preheader: '',
  bg_image: '',
  content_id: '',
  cta_url: '',
  blocks: [
    { type: 'heading', text: 'New from Portal Network', align: 'left' },
    { type: 'text',    text: 'Something big just dropped. Tap the button below to check it out.', align: 'left' },
    { type: 'button',  text: 'Watch Now', url: 'https://pulseportal.tv', align: 'center' },
  ],
  include_users: true,
  include_contacts: true,
  extra_emails: [],
  randomize: false,
  sample_size: null,
});

const ComposeTab = () => {
  const [form, setForm] = useState(emptyBlast());
  const [titles, setTitles] = useState([]);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/admin/email/titles').then(r => setTitles(r.data.titles || [])).catch(()=>{});
  }, []);

  const update = (patch) => setForm(f => ({ ...f, ...patch }));
  const updateBlock = (i, patch) => setForm(f => {
    const b = [...f.blocks]; b[i] = { ...b[i], ...patch }; return { ...f, blocks: b };
  });
  const addBlock = (type) => {
    const defaults = {
      heading: { type: 'heading', text: 'Heading', align: 'left' },
      text:    { type: 'text',    text: 'Body text goes here.', align: 'left' },
      image:   { type: 'image',   url: '', alt: '' },
      button:  { type: 'button',  text: 'Watch Now', url: 'https://pulseportal.tv', align: 'center' },
      divider: { type: 'divider' },
    };
    setForm(f => ({ ...f, blocks: [...f.blocks, defaults[type]] }));
  };
  const move = (i, dir) => setForm(f => {
    const b = [...f.blocks]; const j = i + dir;
    if (j < 0 || j >= b.length) return f;
    [b[i], b[j]] = [b[j], b[i]]; return { ...f, blocks: b };
  });
  const removeBlock = (i) => setForm(f => ({ ...f, blocks: f.blocks.filter((_, k) => k !== i) }));

  const doPreview = async () => {
    setError(''); setBusy(true);
    try {
      const { data } = await api.post('/admin/email/preview', form);
      setPreview(data.html);
    } catch (e) { setError(e?.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const doSend = async (testOnly = false) => {
    setError(''); setResult(null); setBusy(true);
    try {
      const payload = { ...form, test_only_to: testOnly ? testTo : null };
      const { data } = await api.post('/admin/email/send', payload);
      setResult(data);
    } catch (e) { setError(e?.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* LEFT: Form */}
      <div className="space-y-4">
        <BlastKindTabs kind={form.kind} onChange={(k) => update({ kind: k })} />

        <Field label="Subject line">
          <input value={form.subject} onChange={e => update({ subject: e.target.value })}
            placeholder="e.g. New from Portal — out now." className={inputCls}
            data-testid="blast-subject" />
        </Field>

        <Field label="Preheader (preview text)">
          <input value={form.preheader} onChange={e => update({ preheader: e.target.value })}
            placeholder="Preview snippet shown in the inbox" className={inputCls} />
        </Field>

        {form.kind === 'title' ? (
          <>
            <Field label="Select Title">
              <select value={form.content_id} onChange={e => update({ content_id: e.target.value })}
                className={inputCls} data-testid="blast-title-select">
                <option value="">-- Choose a title --</option>
                {titles.map(t => (
                  <option key={t.id} value={t.id} className="bg-background">
                    {t.title} {t.coming_soon ? '(coming soon)' : ''} — {t.content_type}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="CTA URL (optional — defaults to /content/{id})">
              <input value={form.cta_url || ''} onChange={e => update({ cta_url: e.target.value })}
                placeholder="https://pulseportal.tv/content/..." className={inputCls} />
            </Field>
            <p className="text-xs text-muted-foreground">
              Auto-fills thumbnail, synopsis, cast, director, release info and adds Watch Now / Remind Me button.
            </p>
          </>
        ) : (
          <>
            <Field label="Background image URL (optional)">
              <input value={form.bg_image || ''} onChange={e => update({ bg_image: e.target.value })}
                placeholder="https://.../background.jpg" className={inputCls} />
            </Field>

            <div className="rounded-xl border border-white/10 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground/80">Content blocks</span>
                <div className="flex gap-1">
                  <MiniBtn onClick={() => addBlock('heading')}>H</MiniBtn>
                  <MiniBtn onClick={() => addBlock('text')}>P</MiniBtn>
                  <MiniBtn onClick={() => addBlock('image')}>Img</MiniBtn>
                  <MiniBtn onClick={() => addBlock('button')}>Btn</MiniBtn>
                  <MiniBtn onClick={() => addBlock('divider')}>—</MiniBtn>
                </div>
              </div>
              {form.blocks.map((b, i) => (
                <BlockEditor key={i} block={b} onChange={(p) => updateBlock(i, p)}
                  onUp={() => move(i, -1)} onDown={() => move(i, 1)}
                  onDelete={() => removeBlock(i)} />
              ))}
            </div>
          </>
        )}

        <div className="rounded-xl border border-white/10 p-3 space-y-2">
          <div className="text-sm font-medium text-foreground/80">Recipients</div>
          <label className="flex items-center gap-2 text-sm text-foreground/80">
            <input type="checkbox" checked={form.include_users}
              onChange={e => update({ include_users: e.target.checked })}
              data-testid="blast-include-users" />
            All signed-up users
          </label>
          <label className="flex items-center gap-2 text-sm text-foreground/80">
            <input type="checkbox" checked={form.include_contacts}
              onChange={e => update({ include_contacts: e.target.checked })} />
            All email contacts (manually added + CSV)
          </label>
          <label className="flex items-center gap-2 text-sm text-foreground/80">
            <input type="checkbox" checked={form.randomize}
              onChange={e => update({ randomize: e.target.checked })}
              data-testid="blast-randomize" />
            <Shuffle className="h-3.5 w-3.5 text-electric" />
            Randomize order &amp; take a sample
          </label>
          {form.randomize && (
            <div className="pl-6">
              <label className="text-xs text-muted-foreground">Sample size</label>
              <input type="number" min="1" value={form.sample_size || 1500}
                onChange={e => update({ sample_size: parseInt(e.target.value) || null })}
                className={inputCls + ' max-w-[140px]'} data-testid="blast-sample-size" />
            </div>
          )}
        </div>

        {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>}
        {result && (
          <div className="p-3 rounded-lg bg-electric/10 border border-electric/30 text-sm">
            ✓ Sent to <b>{result.sent}</b> of {result.total}. {result.failed > 0 && <span className="text-orange-400">({result.failed} failed)</span>}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button disabled={busy} onClick={doPreview}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-foreground hover:bg-white/15 disabled:opacity-50"
            data-testid="blast-preview-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
          </button>
          <div className="flex items-center gap-1">
            <input value={testTo} onChange={e => setTestTo(e.target.value)}
              placeholder="you@example.com"
              className={inputCls + ' max-w-[220px]'} />
            <button disabled={busy || !testTo} onClick={() => doSend(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-foreground/80 hover:bg-white/15 disabled:opacity-50 text-sm">
              Test send
            </button>
          </div>
          <button disabled={busy} onClick={() => doSend(false)}
            className="ml-auto inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-electric text-background font-bold hover:bg-electric/90 disabled:opacity-50"
            data-testid="blast-send-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Blast
          </button>
        </div>
      </div>

      {/* RIGHT: Preview */}
      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-white/[0.02]">
          <span className="text-xs font-mono text-muted-foreground">/ LIVE PREVIEW</span>
          <span className="text-xs text-muted-foreground">Renders as recipients will see it</span>
        </div>
        {preview ? (
          <iframe title="preview" srcDoc={preview} className="w-full bg-black" style={{ height: 780, border: 0 }} />
        ) : (
          <div className="p-16 text-center text-muted-foreground">
            <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
            Tap PREVIEW to render your email.
          </div>
        )}
      </div>
    </div>
  );
};

const BlastKindTabs = ({ kind, onChange }) => (
  <div className="flex items-center gap-2 p-1 rounded-lg bg-white/[0.03] border border-white/10 w-fit">
    {[['custom', 'Custom'], ['title', 'Title Blast']].map(([id, label]) => (
      <button key={id} onClick={() => onChange(id)}
        className={`px-3 py-1.5 rounded-md text-sm font-medium ${
          kind === id ? 'bg-electric text-background' : 'text-foreground/70 hover:text-foreground'
        }`}
        data-testid={`blast-kind-${id}`}
      >{label}</button>
    ))}
  </div>
);

const BlockEditor = ({ block, onChange, onUp, onDown, onDelete }) => {
  return (
    <div className="rounded-lg border border-white/10 p-2.5 bg-white/[0.02] space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase text-electric">{block.type}</span>
        <div className="flex items-center gap-1">
          <MiniBtn onClick={onUp}>↑</MiniBtn>
          <MiniBtn onClick={onDown}>↓</MiniBtn>
          <MiniBtn onClick={onDelete} danger><Trash2 className="h-3 w-3" /></MiniBtn>
        </div>
      </div>
      {block.type === 'heading' && (
        <input value={block.text} onChange={e => onChange({ text: e.target.value })} className={inputCls} placeholder="Heading" />
      )}
      {block.type === 'text' && (
        <textarea value={block.text} onChange={e => onChange({ text: e.target.value })} rows="3" className={inputCls} placeholder="Body text" />
      )}
      {block.type === 'image' && (
        <div className="space-y-1.5">
          <input value={block.url} onChange={e => onChange({ url: e.target.value })} className={inputCls} placeholder="Image URL" />
          <input value={block.alt || ''} onChange={e => onChange({ alt: e.target.value })} className={inputCls} placeholder="Alt text (optional)" />
        </div>
      )}
      {block.type === 'button' && (
        <div className="grid grid-cols-2 gap-1.5">
          <input value={block.text} onChange={e => onChange({ text: e.target.value })} className={inputCls} placeholder="Button text" />
          <input value={block.url} onChange={e => onChange({ url: e.target.value })} className={inputCls} placeholder="https://..." />
        </div>
      )}
      {['heading','text','button'].includes(block.type) && (
        <div className="flex items-center gap-1 pt-1">
          {['left','center','right'].map(a => (
            <button key={a} onClick={() => onChange({ align: a })}
              className={`px-2 py-0.5 text-[10px] uppercase rounded ${block.align === a ? 'bg-electric text-background' : 'bg-white/5 text-muted-foreground'}`}>
              {a}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Contacts Tab ────────────────────────────────────────────────────────

const ContactsTab = () => {
  const [state, setState] = useState({ contacts: [], total: 0, signup_users: 0 });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get('/admin/email/contacts');
    setState(data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try { await api.post('/admin/email/contacts', { email, name: name || null }); setEmail(''); setName(''); await load(); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Remove this contact?')) return;
    await api.delete(`/admin/email/contacts/${id}`); await load();
  };
  const uploadCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const { data } = await api.post('/admin/email/contacts/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(`Imported ${data.imported}, skipped ${data.skipped}`);
      await load();
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Email contacts" value={state.total} />
        <Stat label="Signed-up users" value={state.signup_users} />
        <Stat label="Total reachable (dedup)" value="—" hint="Computed at send time" />
      </div>

      <form onSubmit={add} className="rounded-xl border border-white/10 p-3 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs text-muted-foreground">Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" className={inputCls} required />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs text-muted-foreground">Name (optional)</label>
          <input value={name} onChange={e => setName(e.target.value)} className={inputCls} />
        </div>
        <button disabled={busy} type="submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-electric text-background font-semibold hover:bg-electric/90 disabled:opacity-50">
          <Plus className="h-4 w-4" /> Add
        </button>
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-foreground hover:bg-white/15 cursor-pointer">
          <Upload className="h-4 w-4" /> Import CSV
          <input ref={fileRef} type="file" accept=".csv" onChange={uploadCsv} hidden />
        </label>
      </form>

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-muted-foreground text-left">
            <tr><th className="px-3 py-2">Email</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Source</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {state.contacts.length === 0 ? (
              <tr><td colSpan="4" className="px-3 py-8 text-center text-muted-foreground">No contacts yet — add one above or import a CSV.</td></tr>
            ) : state.contacts.map(c => (
              <tr key={c.id} className="border-t border-white/5">
                <td className="px-3 py-2 text-foreground">{c.email}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.name || '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.source}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => del(c.id)} className="p-1 text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Recurring Tab ───────────────────────────────────────────────────────

const RecurringTab = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => { const { data } = await api.get('/admin/email/recurring'); setRows(data.recurring || []); };
  useEffect(() => { load(); }, []);

  const toggle = async (id) => { await api.post(`/admin/email/recurring/${id}/toggle`); await load(); };
  const runNow = async (id) => {
    if (!window.confirm('Run this blast now?')) return;
    const { data } = await api.post(`/admin/email/recurring/${id}/run-now`);
    alert(`Sent ${data.sent} of ${data.total}`); await load();
  };
  const del = async (id) => { if (!window.confirm('Delete this recurring blast?')) return; await api.delete(`/admin/email/recurring/${id}`); await load(); };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Recurring blasts run on a schedule (America/New_York). Each run re-selects recipients — pair with <b className="text-electric">Randomize</b> to draw a fresh N contacts each cycle.
        </p>
        <button onClick={() => setEditing({})}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-electric text-background font-semibold hover:bg-electric/90"
          data-testid="new-recurring-btn">
          <Plus className="h-4 w-4" /> New recurring
        </button>
      </div>

      <div className="rounded-xl border border-white/10 divide-y divide-white/5">
        {rows.length === 0 && <div className="p-8 text-center text-muted-foreground">No recurring blasts yet.</div>}
        {rows.map(r => (
          <div key={r.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${r.enabled ? 'bg-green-400' : 'bg-white/20'}`} />
                <b className="text-foreground">{r.name}</b>
                {r.randomize && <span className="text-[10px] px-2 py-0.5 rounded-full bg-electric/20 text-electric font-bold">{r.sample_size || '?'} RANDOM</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                <Clock className="h-3 w-3" /> Weekly · {r.weekday.toUpperCase()} @ {String(r.hour).padStart(2,'0')}:{String(r.minute).padStart(2,'0')} · {r.timezone} · Runs: {r.runs || 0}
                {r.last_run_at && <> · Last: {new Date(r.last_run_at).toLocaleString()}</>}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">Subject: {r.subject}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggle(r.id)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm ${r.enabled ? 'border-yellow-500/40 text-yellow-300' : 'border-green-500/40 text-green-300'}`}>
                {r.enabled ? <><Pause className="h-3.5 w-3.5" /> Pause</> : <><Play className="h-3.5 w-3.5" /> Enable</>}
              </button>
              <button onClick={() => runNow(r.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-electric/40 text-electric text-sm">
                <RefreshCw className="h-3.5 w-3.5" /> Run now
              </button>
              <button onClick={() => setEditing(r)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/20 text-foreground/80 text-sm">Edit</button>
              <button onClick={() => del(r.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-500/40 text-red-300 text-sm">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <RecurringModal initial={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />
      )}
    </div>
  );
};

const RecurringModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => ({
    name: '', subject: '', preheader: '',
    kind: 'custom', content_id: '', cta_url: '',
    blocks: emptyBlast().blocks, bg_image: '',
    include_users: true, include_contacts: true,
    randomize: true, sample_size: 1500,
    frequency: 'weekly', weekday: 'mon', hour: 10, minute: 0,
    timezone: 'America/New_York', enabled: true,
    ...(initial || {}),
  }));
  const [titles, setTitles] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/admin/email/titles').then(r => setTitles(r.data.titles || [])).catch(()=>{}); }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) await api.put(`/admin/email/recurring/${initial.id}`, form);
      else await api.post('/admin/email/recurring', form);
      await onSaved();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-3">
        <h3 className="text-lg font-bold text-foreground">{isEdit ? 'Edit' : 'New'} recurring blast</h3>

        <Field label="Name (internal)"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Kind">
            <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))} className={inputCls}>
              <option value="custom" className="bg-background">Custom</option>
              <option value="title" className="bg-background">Title Blast</option>
            </select>
          </Field>
          <Field label="Sample size (random)"><input type="number" value={form.sample_size || 0} onChange={e => setForm(f => ({ ...f, sample_size: parseInt(e.target.value) || 0 }))} className={inputCls} /></Field>
        </div>
        <Field label="Subject line"><input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className={inputCls} /></Field>
        <Field label="Preheader"><input value={form.preheader} onChange={e => setForm(f => ({ ...f, preheader: e.target.value }))} className={inputCls} /></Field>

        {form.kind === 'title' && (
          <Field label="Title">
            <select value={form.content_id} onChange={e => setForm(f => ({ ...f, content_id: e.target.value }))} className={inputCls}>
              <option value="">-- Choose a title --</option>
              {titles.map(t => <option key={t.id} value={t.id} className="bg-background">{t.title}</option>)}
            </select>
          </Field>
        )}

        <div className="grid grid-cols-4 gap-2">
          <Field label="Weekday">
            <select value={form.weekday} onChange={e => setForm(f => ({ ...f, weekday: e.target.value }))} className={inputCls}>
              {['mon','tue','wed','thu','fri','sat','sun'].map(d => <option key={d} value={d} className="bg-background">{d.toUpperCase()}</option>)}
            </select>
          </Field>
          <Field label="Hour"><input type="number" min="0" max="23" value={form.hour} onChange={e => setForm(f => ({ ...f, hour: parseInt(e.target.value) || 0 }))} className={inputCls} /></Field>
          <Field label="Minute"><input type="number" min="0" max="59" value={form.minute} onChange={e => setForm(f => ({ ...f, minute: parseInt(e.target.value) || 0 }))} className={inputCls} /></Field>
          <Field label="Enabled">
            <label className="flex items-center gap-2 text-sm h-10">
              <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
              on/off
            </label>
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-foreground/80">Cancel</button>
          <button onClick={save} disabled={busy || !form.name || !form.subject}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-electric text-background font-bold disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── History Tab ─────────────────────────────────────────────────────────

const HistoryTab = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/admin/email/history').then(r => setRows(r.data.history || [])).catch(()=>{}); }, []);
  return (
    <div className="rounded-xl border border-white/10 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-muted-foreground text-left">
          <tr><th className="px-3 py-2">Sent</th><th className="px-3 py-2">Kind</th><th className="px-3 py-2">Subject</th><th className="px-3 py-2 text-right">Sent</th><th className="px-3 py-2 text-right">Failed</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan="5" className="px-3 py-8 text-center text-muted-foreground">No blasts sent yet.</td></tr>
          ) : rows.map(r => (
            <tr key={r.id} className="border-t border-white/5">
              <td className="px-3 py-2 text-muted-foreground">{new Date(r.sent_at).toLocaleString()}</td>
              <td className="px-3 py-2 text-electric uppercase text-xs font-bold">{r.kind}</td>
              <td className="px-3 py-2 text-foreground">{r.subject}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.sent_count}</td>
              <td className="px-3 py-2 text-right tabular-nums text-orange-400">{r.failed_count || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── UI helpers ──────────────────────────────────────────────────────────

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/5 border border-white/20 text-foreground focus:outline-none focus:ring-2 focus:ring-electric text-sm';
const Field = ({ label, children }) => (
  <div><label className="text-xs text-muted-foreground block mb-1 font-mono uppercase tracking-wider">{label}</label>{children}</div>
);
const MiniBtn = ({ children, onClick, danger }) => (
  <button onClick={onClick}
    className={`px-2 py-1 rounded text-xs ${danger ? 'text-red-400 hover:bg-red-500/10' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'}`}>
    {children}
  </button>
);
const Stat = ({ label, value, hint }) => (
  <div className="rounded-xl border border-white/10 p-3 bg-white/[0.02]">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-2xl font-bold text-foreground tabular-nums">{value}</div>
    {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
  </div>
);

export default EmailBlasts;
